import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StatusUpdateModal = ({ order, onClose, onUpdateStatus, isUpdating }) => {
  const statusOptions = [
    {
      value: 'pending',
      label: 'Pending',
      description: 'Order received, awaiting confirmation',
      icon: 'Clock',
      color: 'var(--color-warning)'
    },
    {
      value: 'preparing',
      label: 'Preparing',
      description: 'Order confirmed and currently cooking',
      icon: 'ChefHat',
      color: 'var(--color-primary)'
    },
    {
      value: 'ready',
      label: 'Ready',
      description: 'Order ready for pickup/delivery',
      icon: 'Package',
      color: 'var(--color-info)'
    },
    {
      value: 'delivered',
      label: 'Delivered',
      description: 'Order completed successfully',
      icon: 'CheckCircle2',
      color: 'var(--color-success)'
    },
    {
      value: 'cancelled',
      label: 'Cancelled',
      description: 'Order cancelled',
      icon: 'XCircle',
      color: 'var(--color-destructive)'
    }
  ];

  const handleStatusClick = (newStatus) => {
    if (newStatus !== order?.status) {
      onUpdateStatus(order?.orderId, newStatus);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl shadow-warm-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="font-heading font-bold text-xl text-foreground mb-1">
              Update Order Status
            </h2>
            <p className="text-sm text-muted-foreground">
              Order #{order?.orderId} - {order?.customerName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            disabled={isUpdating}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Order Summary */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-4 mb-4">
            <img
              src={order?.customerImage}
              alt={order?.customerImageAlt}
              className="w-12 h-12 rounded-full object-cover"
            />
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">{order?.customerName}</h3>
              <p className="text-sm text-muted-foreground">
                {order?.items?.length} item(s) • ${Number(order?.totalAmount ?? 0).toFixed(2)}
              </p>
            </div>
          </div>
          
          {order?.deliveryAddress && (
            <div className="flex items-start gap-2 text-sm text-muted-foreground mb-2">
              <Icon name="MapPin" size={16} className="mt-0.5 flex-shrink-0" />
              <span>{order?.deliveryAddress}</span>
            </div>
          )}
          
          {order?.deliveryTime && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="Clock" size={16} />
              <span>Scheduled: {new Date(order?.deliveryTime)?.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Status Options */}
        <div className="p-6">
          <h3 className="font-semibold text-foreground mb-4">Select New Status</h3>
          <div className="space-y-3">
            {statusOptions?.map((status) => {
              const isCurrentStatus = status?.value === order?.status;
              const isDisabled = isUpdating;
              
              return (
                <button
                  key={status?.value}
                  onClick={() => handleStatusClick(status?.value)}
                  disabled={isDisabled}
                  className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                    isCurrentStatus
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted'
                  } ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="p-2 rounded-lg flex-shrink-0"
                      style={{ backgroundColor: `${status?.color}15` }}
                    >
                      <Icon name={status?.icon} size={20} color={status?.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-foreground">{status?.label}</h4>
                        {isCurrentStatus && (
                          <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full font-medium">
                            Current
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{status?.description}</p>
                    </div>
                    {!isCurrentStatus && (
                      <Icon name="ChevronRight" size={20} className="text-muted-foreground flex-shrink-0" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex gap-3">
          <Button
            variant="outline"
            fullWidth
            onClick={onClose}
            disabled={isUpdating}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StatusUpdateModal;